/**
 * セッションストレージのキー定義
 * 新しいキーを追加する場合は、ここに型とデフォルト値を定義する
 */
type SessionStorageSchema = {
  'debug-empty-mode': boolean;
  /** Feature Flag のデバッグ上書き（shared層のためRecord<string, string>で定義） */
  'debug-feature-flags': Record<string, string>;
  /** Release Flag のデバッグ上書き（shared層のためRecord<string, boolean>で定義） */
  'debug-release-flags': Record<string, boolean>;
  /** 精度確認モードのON/OFF */
  'debug-accuracy-check-mode': boolean;
  /** AIトークンエラーモードのON/OFF */
  'debug-ai-token-error-mode': boolean;
  /** トークン使用率オーバーライド（null: オーバーライドなし、0-120: 使用率%） */
  'debug-token-usage-percentage': number | null;
  /** 都道府県補助金マッチングの利用可能状態を上書き（'default' | 'enabled' | 'disabled'） */
  'debug-prefectural-grants-override': 'default' | 'enabled' | 'disabled';
  /** ログインセッション中フラグ（ブラウザを閉じると消える。初回パスワード変更フローの制御に使用） */
  'login-session-active': boolean;
};

/**
 * セッションストレージのデフォルト値定義
 * 新しいキーを追加する場合は、ここにデフォルト値も定義する
 */
const SESSION_STORAGE_DEFAULTS: SessionStorageSchema = {
  'debug-empty-mode': false,
  'debug-feature-flags': {},
  'debug-release-flags': {},
  'debug-accuracy-check-mode': false,
  'debug-ai-token-error-mode': false,
  'debug-token-usage-percentage': null,
  'debug-prefectural-grants-override': 'default',
  'login-session-active': false,
};

/**
 * セッションストレージのキー一覧
 */
export type SessionStorageKey = keyof SessionStorageSchema;

/**
 * 指定されたキーに対応する値の型
 */
export type SessionStorageValue<K extends SessionStorageKey> = SessionStorageSchema[K];

/**
 * セッションストレージから値を取得
 *
 * @param key - セッションストレージのキー
 * @returns セッションストレージの値、または存在しない場合はデフォルト値
 *
 * @example
 * ```ts
 * const isEmptyMode = getSessionStorage('debug-empty-mode');
 * ```
 */
export const getSessionStorage = <K extends SessionStorageKey>(key: K): SessionStorageValue<K> => {
  const defaultValue = SESSION_STORAGE_DEFAULTS[key];

  if (typeof window === 'undefined') {
    return defaultValue;
  }

  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }

    return JSON.parse(item) as SessionStorageValue<K>;
  } catch (_error) {
    return defaultValue;
  }
};

/**
 * セッションストレージに値を設定
 *
 * @param key - セッションストレージのキー
 * @param value - 設定する値
 *
 * @example
 * ```ts
 * setSessionStorage('debug-empty-mode', true);
 * ```
 */
export const setSessionStorage = <K extends SessionStorageKey>(
  key: K,
  value: SessionStorageValue<K>,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {}
};

/**
 * セッションストレージから値を削除
 *
 * @param key - セッションストレージのキー
 *
 * @example
 * ```ts
 * removeSessionStorage('debug-empty-mode');
 * ```
 */
export const removeSessionStorage = (key: SessionStorageKey): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(key);
  } catch (_error) {}
};

/**
 * セッションストレージをすべてクリア
 *
 * @example
 * ```ts
 * clearSessionStorage();
 * ```
 */
export const clearSessionStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.clear();
  } catch (_error) {}
};
